/**
 * The one way a screen reports a request that failed.
 *
 * It shows the message the API chose — already safe to display, because the
 * exception filter replaces anything internal with a generic sentence — and the
 * request id when there is one, so a person can quote it and the line can be
 * found in the server log.
 *
 * `FormAlert` is for a *form's* verdict on what the user just submitted. This is
 * for a screen that could not load what it went to fetch, and it always offers a
 * way back: a failed load with no retry is a dead end.
 *
 * ## ONE ANNOUNCEMENT PER FAILED READ, NOT ONE PER PLACE IT SHOWS — SLICE 25
 *
 * `role="alert"` was unconditional here, and a screen may render this component
 * several times for **one** failure. The Overview is the case that proved it:
 * `GET /overview/activity` feeds the attention slot and both chart cards, so a
 * single failed request mounted three of these, two of them word for word
 * identical, and a screen reader announced the same sentence three times before
 * the reader could reach any of it.
 *
 * Three alerts for one fault is not three times the warning. It is the reason
 * people turn the announcements off, and then miss the one that mattered.
 *
 * So `announce` is a prop and it defaults to `true`: every existing caller keeps
 * the behaviour it had, and a screen that renders this more than once for one
 * read passes `announce={false}` on the repeats. **It changes nothing a sighted
 * reader sees** — every copy keeps its heading, its message, its request id and
 * its retry, because each of those is still the right thing to show in the place
 * it shows. What it removes is a duplicate announcement of a fault that has
 * already been announced.
 *
 * It is not a `live` enum like `Callout`'s. There is exactly one honest value
 * for the first copy of a failed read — `alert`, a fault the reader must not
 * walk away believing succeeded — and offering `polite` here would be offering a
 * way to make a failure quieter than it is.
 */
import { Button } from '@wend/ui';
import { RotateCw, TriangleAlert } from 'lucide-react';

import { errorMessage, isApiError } from '../lib/api/errors';

export interface ErrorStateProps {
  error: unknown;
  title?: string;
  onRetry?: () => void;
  /** Set while the retry is in flight. */
  retrying?: boolean;
  className?: string;
  /**
   * Whether this copy announces itself. Default `true`.
   *
   * Set `false` only on a *repeat* of a failure another mounted `ErrorState`
   * already announces — see the header. Never on the only one on screen: a
   * failed read that announces nothing is a dead end a screen-reader user
   * reaches by scrolling into it.
   */
  announce?: boolean;
}

export function ErrorState({
  error,
  title = 'That did not work',
  onRetry,
  retrying = false,
  className,
  announce = true,
}: ErrorStateProps) {
  const requestId = isApiError(error) ? error.requestId : undefined;

  return (
    <div
      {...(announce ? { role: 'alert' as const } : {})}
      className={className ?? 'flex flex-col items-start gap-3 px-6 py-10'}
    >
      <span className="flex items-center gap-2 font-mono text-[0.72rem] uppercase tracking-[0.12em] text-bad">
        <TriangleAlert aria-hidden="true" className="size-4" />
        {title}
      </span>

      <p className="max-w-prose text-[0.95rem] leading-relaxed text-muted">
        {errorMessage(error)}
      </p>

      {requestId !== undefined && (
        <p className="font-mono text-[0.7rem] tracking-[0.06em] text-muted">Request {requestId}</p>
      )}

      {onRetry !== undefined && (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          loading={retrying}
          leadingIcon={<RotateCw aria-hidden="true" />}
          onClick={onRetry}
        >
          Try again
        </Button>
      )}
    </div>
  );
}
